

import React, { useState, useEffect } from 'react';
import { Link } from "react-router-dom";
import Footer from '../../Footer/Footer';
import Header from '../TopNavbar';

const Breadcrumb = ({ crumbs }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', marginBottom: '8px', flexWrap: 'wrap' }}>
    {crumbs.map((crumb, idx) => (
      <React.Fragment key={idx}>
        {crumb.to ? (
          <Link to={crumb.to} style={{ color: 'rgba(255, 255, 255, 0.8)', textDecoration: 'none' }}>
            {crumb.label}
          </Link>
        ) : (
          <span style={{ color: '#FFD500' }}>{crumb.label}</span>
        )}
        {idx < crumbs.length - 1 && <span style={{ color: 'rgba(255, 255, 255, 0.4)' }}>/</span>}
      </React.Fragment>
    ))}
  </div>
);

const Multiple = () => {
  // State for Multiple Districts Meta API (initialized to null/empty to avoid static text)
  const [multipleInfo, setMultipleInfo] = useState(null);
  const [metaLoading, setMetaLoading] = useState(true);

  // State for Leaders API
  const [leaders, setLeaders] = useState([]);
  const [leadersLoading, setLeadersLoading] = useState(true);

  // State for Districts API
  const [districts, setDistricts] = useState([]);
  const [districtsLoading, setDistrictsLoading] = useState(true);

  // Fetch all backend data on component mount
  useEffect(() => {
    const fetchData = async () => {
      // 1. Fetch Multiple Districts Meta Info
      try {
        setMetaLoading(true);
        const resMeta = await fetch('https://api.lionsmd320.org/pages/api/multiple-districts');
        const dataMeta = await resMeta.json();

        let metaRow = null;
        if (dataMeta.multipleDistricts && dataMeta.multipleDistricts.length > 0) {
          metaRow = dataMeta.multipleDistricts[0];
        } else if (dataMeta.rows && dataMeta.rows.length > 0) {
          metaRow = dataMeta.rows[0];
        } else if (Array.isArray(dataMeta) && dataMeta.length > 0) {
          metaRow = dataMeta[0];
        } else if (dataMeta.title) {
          metaRow = dataMeta;
        }

        if (metaRow) {
          setMultipleInfo(metaRow);
        }
      } catch (err) {
        console.error('Failed to fetch multiple-districts meta:', err);
      } finally {
        setMetaLoading(false);
      }

      // 2. Fetch Districts Directory
      try {
        setDistrictsLoading(true);
        const resDistricts = await fetch('https://api.lionsmd320.org/pages/api/districts');
        const dataDistricts = await resDistricts.json();
        if (dataDistricts.districts) {
          setDistricts(dataDistricts.districts);
        } else if (Array.isArray(dataDistricts)) {
          setDistricts(dataDistricts);
        }
      } catch (err) {
        console.error('Failed to fetch districts:', err);
      } finally {
        setDistrictsLoading(false);
      }

      // 3. Fetch Leaders
      try {
        setLeadersLoading(true);
        const resLeaders = await fetch('https://api.lionsmd320.org/pages/api/leaders');
        const dataLeaders = await resLeaders.json();
        if (dataLeaders.leaders) {
          setLeaders(dataLeaders.leaders);
        } else if (Array.isArray(dataLeaders)) {
          setLeaders(dataLeaders);
        }
      } catch (err) {
        console.error('Failed to fetch leaders:', err);
      } finally {
        setLeadersLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <>
      <Header />

      {/* Responsive Styles Injection */}
      <style>{`
        @media (max-width: 1024px) {
          .responsive-grid-4 {
            grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
          }
          .responsive-grid-3 {
            grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
          }
        }
        @media (max-width: 640px) {
          .responsive-grid-4 {
            grid-template-columns: repeat(1, minmax(0, 1fr)) !important;
          }
          .responsive-grid-3 {
            grid-template-columns: repeat(1, minmax(0, 1fr)) !important;
          }
          .responsive-grid-summary {
            grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            gap: 16px !important;
          }
          .responsive-hero-title {
            font-size: 28px !important;
          }
          .responsive-section-title {
            font-size: 24px !important;
          }
          .responsive-hero-section {
            padding: 32px 0 !important;
          }
          .responsive-section {
            padding: 40px 0 !important;
          }
          .responsive-card-padding {
            padding: 16px !important;
          }
        }
      `}</style>

      <div>
        {/* Hero Section */}
        <div className="responsive-hero-section" style={{ backgroundColor: '#0067B1', padding: '48px 0', textAlign: 'left' }}>
          <div style={{ maxWidth: '1280px', margin: '0 auto', paddingLeft: '16px', paddingRight: '16px', textAlign: 'left' }}>
            <Breadcrumb crumbs={[{ label: 'Home', to: '/' }, { label: multipleInfo?.title || 'Multiple Districts' }]} />
            <h1 className="responsive-hero-title" style={{ fontSize: '36px', fontWeight: 800, color: '#ffffff', marginTop: '16px', textAlign: 'left' }}>
              {metaLoading ? 'Loading...' : (multipleInfo?.title || 'Multiple Districts')}
            </h1>
            <p style={{ color: 'rgba(255, 255, 255, 0.7)', marginTop: '8px', fontSize: '16px', maxWidth: '576px', textAlign: 'left' }}>
              {multipleInfo?.description || 'Loading service area data...'}
            </p>
          </div>
        </div>

        {/* Summary Bar - Driven dynamically by API */}
        <div style={{ backgroundColor: '#005090', borderBottom: '1px solid #0067B1' }}>
          <div style={{ maxWidth: '1280px', margin: '0 auto', paddingLeft: '16px', paddingRight: '16px', paddingTop: '20px', paddingBottom: '20px' }}>
            <div className="responsive-grid-summary" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '24px' }}>
              {[
                { value: districts.length > 0 ? districts.length : (multipleInfo?.districts || '—'), label: "Districts" },
                { value: multipleInfo?.total_clubs ? Number(multipleInfo.total_clubs).toLocaleString() : '—', label: "Total Clubs" },
                { value: multipleInfo?.total_members ? Number(multipleInfo.total_members).toLocaleString() : '—', label: "Total Members" },
                { value: multipleInfo?.fiscal_year || '—', label: "Fiscal Year" },
              ].map((s) => (
                <div key={s.label} style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', fontWeight: 800, color: '#FFD500' }}>{s.value}</div>
                  <div style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '12px', letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 500, marginTop: '2px' }}>{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* About MD 320 Section */}
        {/* className="responsive-section" */}
        <section className="responsive-section" style={{ padding: '64px 0', backgroundColor: '#ffffff' }}>
          <div style={{ maxWidth: '1280px', margin: '0 auto', paddingLeft: '16px', paddingRight: '16px' }}>
            <div className="responsive-card-padding" style={{ background: 'linear-gradient(to bottom right, rgba(0, 103, 177, 0.05), rgba(255, 213, 0, 0.1))', border: '1px solid rgba(0, 103, 177, 0.1)', borderRadius: '16px', padding: '32px' }}>
              <span style={{ color: '#0067B1', fontSize: '12px', fontWeight: 'bold', letterSpacing: '0.1em', textTransform: 'uppercase', borderBottom: '2px solid #FFD500', paddingBottom: '4px' }}>About MD</span>
              <h2 className="responsive-section-title" style={{ fontSize: '30px', fontWeight: 800, color: '#0067B1', marginTop: '16px', marginBottom: '24px' }}>Overview</h2>
              <p style={{ color: '#374151', fontSize: '16px', lineHeight: '1.6' }}>
                {multipleInfo?.description || 'Fetching information about the multiple district...'}
              </p>
            </div>
          </div>
        </section>

        {/* Our Leaders Section */}
        <section className="responsive-section" style={{ padding: '64px 0', backgroundColor: '#f9fafb' }}>
          <div style={{ maxWidth: '1280px', margin: '0 auto', paddingLeft: '16px', paddingRight: '16px' }}>
            <div style={{ textAlign: 'center', marginBottom: '48px' }}>
              <span style={{ color: '#0067B1', fontSize: '12px', fontWeight: 'bold', letterSpacing: '0.1em', textTransform: 'uppercase', borderBottom: '2px solid #FFD500', paddingBottom: '4px' }}>Leadership</span>
              <h2 className="responsive-section-title" style={{ fontSize: '30px', fontWeight: 800, color: '#0067B1', marginTop: '20px' }}>Our Leaders</h2>
            </div>

            {leadersLoading ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280', fontSize: '14px', fontWeight: '600' }}>Loading Leaders...</div>
            ) : leaders.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280', fontSize: '14px', fontWeight: '600' }}>No leaders found.</div>
            ) : (
              <div className="responsive-grid-3" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '32px' }}>
                {leaders.map((leader) => (
                  <div key={leader.id} style={{ backgroundColor: '#ffffff', border: '1px solid #f3f4f6', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '24px', textAlign: 'center' }}>
                    <div style={{ width: '128px', height: '128px', borderRadius: '50%', overflow: 'hidden', marginBottom: '20px', border: '4px solid rgba(255, 213, 0, 0.5)', boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.06)' }}>
                      <img src={leader.image || 'https://via.placeholder.com/128'} alt={leader.name} style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' }} />
                    </div>
                    <h3 style={{ fontSize: '20px', fontWeight: 'bold', color: '#0067B1', marginBottom: '4px' }}>
                      {leader.name}
                    </h3>
                    <p style={{ color: '#6b7280', fontSize: '12px', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                      {leader.designation}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Districts Grid */}
        <section className="responsive-section" style={{ paddingBottom: '64px', backgroundColor: '#ffffff' }}>
          <div style={{ maxWidth: '1280px', margin: '0 auto', paddingLeft: '16px', paddingRight: '16px' }}>
            <div style={{ textAlign: 'center', marginBottom: '48px' }}>
              <span style={{ color: '#0067B1', fontSize: '12px', fontWeight: 'bold', letterSpacing: '0.1em', textTransform: 'uppercase', borderBottom: '2px solid #FFD500', paddingBottom: '4px' }}>Our Districts</span>
              <h2 className="responsive-section-title" style={{ fontSize: '30px', fontWeight: 800, color: '#0067B1', marginTop: '20px' }}>District Directory</h2>
            </div>

            {districtsLoading ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280', fontSize: '14px', fontWeight: '600' }}>
                Loading Districts...
              </div>
            ) : districts.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280', fontSize: '14px', fontWeight: '600' }}>
                No districts found.
              </div>
            ) : (
              <div className="responsive-grid-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '24px' }}>
                {districts.map((d) => {
                  const districtSlug = d.district ? d.district.toLowerCase() : d.id;
                  return (
                    <Link key={d.id} to={`/district/${districtSlug}`} style={{ border: '1px solid #f3f4f6', borderRadius: '12px', padding: '24px', backgroundColor: '#ffffff', textDecoration: 'none', display: 'flex', flexDirection: 'column', transition: 'all 0.2s ease', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                      <div style={{ height: '4px', width: '100%', backgroundColor: '#FFD500', borderRadius: '9999px', marginBottom: '20px' }} />
                      <div style={{ fontSize: '36px', fontWeight: 900, color: '#0067B1', marginBottom: '4px' }}>{d.district}</div>
                      <div style={{ color: '#9ca3af', fontSize: '12px', fontWeight: 500, letterSpacing: '0.05em', marginBottom: '20px' }}>{d.district_name}</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '24px', flexGrow: 1 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
                          <span style={{ color: '#9ca3af' }}>Clubs</span>
                          <span style={{ fontWeight: 600, color: '#374151' }}>{d.clubs}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
                          <span style={{ color: '#9ca3af' }}>Members</span>
                          <span style={{ fontWeight: 600, color: '#374151' }}>{d.members ? Number(d.members).toLocaleString() : 0}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
                          <span style={{ color: '#9ca3af' }}>District Chair</span>
                          <span style={{ fontWeight: 600, color: '#374151', textAlign: 'right' }}>{d.district_chairperson || 'N/A'}</span>
                        </div>
                      </div>
                      <div style={{ backgroundColor: '#0067B1', color: '#ffffff', fontSize: '12px', fontWeight: 'bold', letterSpacing: '0.1em', textTransform: 'uppercase', padding: '10px 0', borderRadius: '4px', textAlign: 'center' }}>View District</div>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        <section className="responsive-section" style={{ paddingBottom: '64px', backgroundColor: '#ffffff' }}>
          <div style={{ maxWidth: '1280px', margin: '0 auto', paddingLeft: '16px', paddingRight: '16px' }}>
            <div style={{ textAlign: 'center', marginBottom: '48px' }}>
              <span style={{ color: '#0067B1', fontSize: '12px', fontWeight: 'bold', letterSpacing: '0.1em', textTransform: 'uppercase', borderBottom: '2px solid #FFD500', paddingBottom: '4px' }}>Leadership Team</span>
              <h2 className="responsive-section-title" style={{ fontSize: '30px', fontWeight: 800, color: '#0067B1', marginTop: '20px' }}>GAT & COLLABORATIVE GAT</h2>
            </div>

            <div className="responsive-grid-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '24px' }}>
              {[
                { role: 'GET', name: 'Dr. G. Srinivas Reddy' },
                { role: 'GMT', name: 'P. Basveswara Rao' },
                { role: 'GLT', name: 'S. N. Reddy' },
                { role: 'GST', name: 'Teegala Mohan Rao' },
                { role: 'MARKETING', name: 'Ramesh Lakshmanan' },
                { role: 'LCIF', name: 'K. Harish Reddy' },
                { role: 'FWC', name: 'Sunitha Anand' },
                { role: 'LEO', name: 'Venkat Reddy' },
                { role: 'QUEST', name: 'Mucha Raji Reddy' }
              ].map((item, index) => {
                // Create a URL-friendly slug from the role
                const roleSlug = item.role.toLowerCase().replace(/[^a-z0-9]+/g, '-');

                return (
                  <Link
                    key={index}
                    to={`/leadership/${roleSlug}`}
                    style={{
                      border: '1px solid #f3f4f6',
                      borderRadius: '12px',
                      padding: '24px',
                      backgroundColor: '#ffffff',
                      textDecoration: 'none',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                    }}
                  >
                    <div style={{ height: '4px', width: '100%', backgroundColor: '#FFD500', borderRadius: '9999px', marginBottom: '20px' }} />
                    <div style={{ color: '#9ca3af', fontSize: '12px', fontWeight: 600, letterSpacing: '0.05em', marginBottom: '8px', textTransform: 'uppercase' }}>{item.role}</div>
                    <div style={{ fontSize: '20px', fontWeight: 700, color: '#0067B1', marginBottom: '24px', flexGrow: 1 }}>{item.name}</div>
                    <div style={{ backgroundColor: '#0067B1', color: '#ffffff', fontSize: '12px', fontWeight: 'bold', letterSpacing: '0.1em', textTransform: 'uppercase', padding: '10px 0', borderRadius: '4px', textAlign: 'center' }}>VIEW</div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

      </div>
      <Footer />
    </>
  );
};

export default Multiple;